import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useAuth } from "utils/useAuth/useAuth";
import { PortalContext } from "App";
import { checkNonMoneyDataForTable, isNullish, trimObject } from "utils/utils";
import { searchErrorHandler } from "utils/formValidation";
import useThrottleCallback from "utils/useThrottleCallback/useThrottleCallback";
import Grid from "components/Grid/Grid";
import Selection from "components/Input/Selection/Selection";
import Datepicker from "components/Input/Datepicker/Datepicker";
import SearchFieldButtonBar from "components/SearchFieldButtonBar/SearchFieldButtonBar";
import Divider from "components/Divider/Divider";
import FunctionalButtonBar from "components/FunctionalButtonBar/FunctionalButtonBar";
import Spin from "components/Spin/Spin";
import Datatable from "components/Datatable/Datatable";
import transactionCurrencyOptions from "utils/transactionCurrencyOptions/TransactionCurrencyOptions.jsx";
import SimpleDialog from "components/SimpleDialog/SimpleDialog";
import ChangeContent from "./components/ChangeContent/ChangeContent";
import CreateContent from "./components/CreateContent/CreateContent";
import EditDialog from "components/EditDialog/EditDialog";
import ConfigContent from "./components/ConfigContent/ConfigContent";
import ArticleIcon from "@mui/icons-material/Article";
import s from "./FxRate.module.scss";
import {
  addFxRate,
  queryList,
  updateFxRate,
  updateFxRateConfig,
} from "./model";
import useColors from "utils/useColors/useColors";

const FxRate = ({ path }) => {
  const c = useColors();
  const { t } = useTranslation();
  const { logout } = useAuth();
  const tabContext = useContext(PortalContext);
  const { currentPath } = tabContext;
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "PaymentGatewayCost";
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [collapse, setCollapse] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searching, setSearching] = useState(false);
  const [datasource, setDatasource] = useState({
    data: [],
    page: 0,
    total: 0,
  });
  const [search, setSearch] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const pageSizeInfoRef = useRef({
    currentpage: 0,
    pagesize: 20,
  });

  const [searchFieldInput, setSearchFieldInput] = useState({
    startDate: null,
    endDate: null,
    currencyCode: "",
  });
  const [focus, setFocus] = useState({});
  const [error, setError] = useState({
    startDate: false,
    endDate: false,
    startDateLaterThanEndDate: false,
  });

  const [columnsArray, setColumnsArray] = useState([]);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [isChangeDialogOpen, setIsChangeDialogOpen] = useState(false);
  const [changeDialogData, setChangeDialogData] = useState({});
  const [changeDialogError, setChangeDialogError] = useState({});
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [createDialogData, setCreateDialogData] = useState({});
  const [isConfigDialogOpen, setIsConfigDialogOpen] = useState(false);
  const [isConfigEditing, setIsConfigEditing] = useState(false);
  const [editConfigData, setEditConfigData] = useState({});

  const windowResizeHandler = useThrottleCallback(() => {
    setWindowWidth(window.innerWidth);
  }, 20);
  useEffect(() => {
    if (currentPath === path) {
      const maxHeight = 76;
      const grid = document.getElementById(gridId);
      if (grid.scrollHeight - 15 <= maxHeight) {
        setCollapse(undefined);
      } else {
        if (collapse == undefined || collapse) {
          setCollapse(true);
        } else {
          setCollapse(false);
        }
      }
    }
    window.addEventListener("resize", windowResizeHandler);
    return () => {
      removeEventListener("resize", windowResizeHandler);
    };
  }, [windowWidth, currentPath]);

  useEffect(() => {
    const init = async () => {
      await initialFetch();
    };
    init();
  }, []);

  const handleQueryListUIResponse = (apiResponse) => {
    if (!apiResponse || !apiResponse?.data) {
      return snackbar(t("general.message.dynamicNetworkError", { data: `${t("page.finance.fxRate.sectionName")}` }), "error");
    }
    const { status, data: responseBody } = apiResponse;

    if (status === 401) {
      console.log("You have no authorization to view the page");
      return logout("TOKEN_EXPIRED");
    }

    if (status === 403) {
      snackbar(t("general.message.unauthorized"), "error");
      return;
    }

    switch (responseBody.responseCode) {
      case "0000":
        if (responseBody?.data?.length === 0) {
          snackbar(t("general.message.no.result"), "warning");
        }
        setDatasource(responseBody);
        setCurrentPage(responseBody.page);
        setPageSize(responseBody.size);
        Object.assign(pageSizeInfoRef.current, {
          currentpage: responseBody.page,
          pagesize: responseBody.size,
        });
        break;

      case "2408":
        snackbar(t("general.message.no.result"), "warning");
        setDatasource({ data: [], page: 0, total: 0 });
        setCurrentPage(0);
        setPageSize(20);
        Object.assign(pageSizeInfoRef.current, {
          currentpage: 0,
          pagesize: 20,
        });
        break;

      default:
        snackbar(t("general.message.networkError"), "error");
        break;
    }
  };

  const initialFetch = async () => {
    setLoading(true);
    const apiResponse = await queryList({
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
    });
    handleQueryListUIResponse(apiResponse);
    setSearch(false);
    setLoading(false);
  };

  const resetInputField = () => {
    setSearchFieldInput({
      startDate: null,
      endDate: null,
      currencyCode: "",
    });
  };

  const resetError = () => {
    setError({
      startDate: false,
      endDate: false,
      startDateLaterThanEndDate: false,
    });
  };

  const handleSearchFieldCollapse = (status) => {
    setCollapse(status);
  };

  const handleReset = async () => {
    resetInputField();
    resetError();
    await initialFetch();
  };

  const handleSearch = async (action) => {
    if (action === "search") {
      const { errorObj, hasError } = searchErrorHandler(searchFieldInput, [
        (input) =>
          ((input.startDate && !input.endDate) || input.endDate === "") && {
            endDate: true,
          },
        (input) =>
          ((input.endDate && !input.startDate) || input.startDate === "") && {
            startDate: true,
          },
        (input) =>
          moment(input.startDate).isAfter(input.endDate) && {
            startDateLaterThanEndDate: true,
          },
      ]);
      setError(errorObj);
      if (hasError) return;

      setCurrentPage(0);
      pageSizeInfoRef.current.currentpage = 0;
    }
    setSearching(true);
    const params = trimObject({ ...searchFieldInput });
    const payload = {
      page: pageSizeInfoRef.current.currentpage,
      size: pageSizeInfoRef.current.pagesize,
      ...params,
    };
    const apiResponse = await queryList(payload);
    handleQueryListUIResponse(apiResponse);
    setSearch(true);
    setSearching(false);
  };

  // ===================== change funciton ===================== //
  const openChangeDialog = (data) => {
    setChangeDialogData({
      ...data,
      exchangeDate: moment(data.exchangeDate).format("YYYY-MM-DD"),
      currencyCode: data.currencyCode,
    });
    setIsChangeDialogOpen(true);
    setDialogLoading(false);
  };

  const closeChangeDialog = () => {
    restoreChangeFormInput();
    setIsChangeDialogOpen(false);
    setDialogLoading(false);
  };

  const handleChangeFormInput = (data, field) => {
    setChangeDialogData({
      ...changeDialogData,
      [field]: data,
    });
  };

  const restoreChangeFormInput = () => {
    setChangeDialogData({});
    setChangeDialogError({});
  };

  const changeContent = () => {
    return (
      <ChangeContent
        handleInput={handleChangeFormInput}
        setError={setChangeDialogError}
        data={changeDialogData}
        error={changeDialogError}
      />
    );
  };

  const changeFxRate = async () => {
    const payload = trimObject({ ...changeDialogData });
    const apiResponse = await updateFxRate(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.change.function.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("general.message.change.function.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmChange = async () => {
    let errorObj = {};
    const isAmountValid = (amount) => {
      return !isNaN(amount) && Number(amount) > 0;
    };

    if (isNullish(changeDialogData.cost)) {
      errorObj = { ...errorObj, costMissing: true };
    } else if (!isAmountValid(changeDialogData.cost)) {
      errorObj = { ...errorObj, costInvalid: true };
    }
    if (isNullish(changeDialogData.fxRate)) {
      errorObj = { ...errorObj, fxRateMissing: true };
    } else if (!isAmountValid(changeDialogData.fxRate)) {
      errorObj = { ...errorObj, fxRateInvalid: true };
    }
    if (isNullish(changeDialogData.bankSellCost)) {
      errorObj = { ...errorObj, bankSellCostMissing: true };
    } else if (!isAmountValid(changeDialogData.bankSellCost)) {
      errorObj = { ...errorObj, bankSellCostInvalid: true };
    }
    if (isNullish(changeDialogData.bankSellFxRate)) {
      errorObj = { ...errorObj, bankSellFxRateMissing: true };
    } else if (!isAmountValid(changeDialogData.bankSellFxRate)) {
      errorObj = { ...errorObj, bankSellFxRateInvalid: true };
    }
    setChangeDialogError(errorObj);
    if (Object.keys(errorObj).length > 0) {
      return;
    }
    setDialogLoading(true);
    const result = await changeFxRate();
    closeChangeDialog();
    if (result) {
      return handleSearch("search");
    }
  };

  // ===================== create funciton ===================== //
  const openCreateDialog = () => {
    setCreateDialogData({
      currencyCode: "USD",
    });
    setIsCreateDialogOpen(true);
    setDialogLoading(false);
  };

  const closeCreateDialog = () => {
    restoreCreateFormInput();
    setIsCreateDialogOpen(false);
    setDialogLoading(false);
  };

  const handleCreateFormInput = (data, field) => {
    setCreateDialogData({
      ...createDialogData,
      [field]: data,
    });
  };

  const createContent = () => {
    return (
      <CreateContent
        handleInput={handleCreateFormInput}
        data={createDialogData}
      />
    );
  };

  const restoreCreateFormInput = () => {
    setCreateDialogData({});
  };

  const createFxRate = async () => {
    const payload = trimObject({ ...createDialogData });
    const apiResponse = await addFxRate(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("general.message.create.function.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("general.message.create.function.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const confirmCreate = async () => {
    if (!createDialogData.exchangeDate) {
      snackbar(
        t("page.finance.fxRate.create.function.error.message.1"),
        "warning",
      );
      return;
    }
    if (!createDialogData.cost) {
      snackbar(
        t("page.finance.fxRate.create.function.error.message.2"),
        "warning",
      );
      return;
    }
    if (!createDialogData.fxRate) {
      snackbar(
        t("page.finance.fxRate.create.function.error.message.3"),
        "warning",
      );
      return;
    }
    if (!createDialogData.bankSellCost) {
      snackbar(
        t("page.finance.fxRate.create.function.error.message.4"),
        "warning",
      );
      return;
    }
    if (!createDialogData.bankSellFxRate) {
      snackbar(
        t("page.finance.fxRate.create.function.error.message.5"),
        "warning",
      );
      return;
    }
    setDialogLoading(true);
    const result = await createFxRate();
    closeCreateDialog();
    if (result) {
      return handleSearch("search");
    }
  };

  // ===================== fx rate config funciton ===================== //
  const openConfigDialog = () => {
    setIsConfigDialogOpen(true);
    setDialogLoading(false);
  };

  const closeConfigDialog = () => {
    setDialogLoading(false);
    setIsConfigDialogOpen(false);
    setIsConfigEditing(false);
    restoreConfigData();
  };

  const handleConfigFormatting = (array) => {
    setEditConfigData(array);
  };

  const handleConfigInput = (data, field) => {
    setEditConfigData({
      ...editConfigData,
      [field]: data,
    });
  };

  const restoreConfigData = () => {
    setEditConfigData({});
  };

  const configContent = () => {
    return (
      <ConfigContent
        handleInput={handleConfigInput}
        handleDataFormat={handleConfigFormatting}
        isDialogEditing={isConfigEditing}
      />
    );
  };

  const handleConfigDialogEditBtn = () => {
    if (isConfigEditing) {
      setIsConfigEditing(false);
    } else {
      setIsConfigEditing(true);
    }
  };

  const fxRateConfigIcon = () => {
    return <ArticleIcon sx={{ fontSize: 16, color: c.brandDark12 }} />;
  };

  const handleUpdateConfig = async (params) => {
    const payload = { fxRateConfigList: params };
    const apiResponse = await updateFxRateConfig(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            snackbar(
              t("page.finance.fxRate.fx.rate.config.success.message"),
              "success",
            );
            return true;
          } else {
            snackbar(
              t("age.finance.fxRate.fx.rate.config.fail.message"),
              "error",
            );
            return false;
          }
        }
      }
    }
  };

  const handleConfigDialogSaveBtn = async () => {
    const currencyList = [
      "USD",
      "TWD",
      "THB",
      "SGD",
      "KRW",
      "JPY",
      "GBP",
      "EUR",
      "CAD",
      "AUD",
    ];
    let id = 1;
    const params = [];
    for (const currency of currencyList) {
      const tempObj = {
        currencyCode: currency,
        editable: true,
        id,
        rate: editConfigData[currency],
      };
      params.push(tempObj);
      id++;
    }
    setDialogLoading(true);
    await handleUpdateConfig(params);
    closeConfigDialog();
  };

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  const textInputOnchange = (e, field, callback) => {
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setSearchFieldInput({ ...searchFieldInput, [field]: value });
  };
  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };
  const textInputOnBlur = (field, callback) => {
    if (searchFieldInput[field] && callback) {
      const isValidValue = callback(searchFieldInput[field]);
      if (!isValidValue) {
        setError({ ...error, [field]: error });
      }
    }
    setFocus({ [field]: false });
  };

  const transactionDateOnChange = (e, type) => {
    const rawDate = JSON.parse(JSON.stringify(e));
    const date = moment(rawDate).isValid()
      ? moment(rawDate).format("YYYY-MM-DD")
      : e !== null
        ? ""
        : null;
    if (type === "start") {
      setError({
        ...error,
        startDate: false,
        startDateLaterThanEndDate: false,
      });
      setSearchFieldInput({ ...searchFieldInput, startDate: date });
    } else if (type === "end") {
      setError({ ...error, endDate: false, startDateLaterThanEndDate: false });
      setSearchFieldInput({ ...searchFieldInput, endDate: date });
    } else {
      return;
    }
  };
  const dateFormatValidator = (dateValue) => {
    const isValidDateFormat = moment(dateValue).isValid();
    return isValidDateFormat;
  };

  const columns = [
    {
      index: 0,
      key: "exchangeDate",
      title: t("page.finance.fxRate.column.title.exchange.date"),
      sorter: false,
      render: (data, key) => {
        return moment(data[key]).format("YYYY-MM-DD");
      },
    },
    {
      index: 1,
      key: "currencyCode",
      title: t("page.finance.fxRate.column.title.currency.code"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 2,
      key: "cost",
      type: "amount",
      title: t("page.finance.fxRate.column.title.bank.buy.cost"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 3,
      key: "fxRate",
      type: "amount",
      title: t("page.finance.fxRate.column.title.bank.buy.fx.rate"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 4,
      key: "bankSellCost",
      type: "amount",
      title: t("page.finance.fxRate.column.title.bank.sell.cost"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 5,
      key: "bankSellFxRate",
      type: "amount",
      title: t("page.finance.fxRate.column.title.bank.sell.fx.rate"),
      sorter: false,
      render: (data, key) => {
        return checkNonMoneyDataForTable(data, key);
      },
    },
    {
      index: 6,
      key: "action",
      title: t("page.finance.fxRate.column.title.action"),
      sorter: false,
      render: (data) => {
        return (
          data.exchangeDate &&
          moment().subtract(2, "day").isBefore(data.exchangeDate, "day") && (
            <div className={s.actionBtnContainer}>
              <button
                onClick={() => {
                  return openChangeDialog(data);
                }}
              >
                {t("page.finance.fxRate.column.title.action.1")}
              </button>
            </div>
          )
        );
      },
    },
  ];
  const pagination = useMemo(() => {
    return {
      currentPage: currentPage,
      pageSize: pageSize,
      pageSizeOptions: [10, 20, 50, 100],
    };
  }, [currentPage, pageSize]);

  const searchField = () => {
    return (
      <div className={s.searchFieldContainer}>
        <Grid gridId={gridId} collapse={collapse}>
          <Datepicker
            label={t("page.finance.fxRate.exchange.start.date.label")}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t("page.finance.fxRate.exchange.start.date.error.message.2")
                : t("page.finance.fxRate.exchange.start.date.error.message")
            }
            type="start"
            value={searchFieldInput.startDate}
            limitValue={searchFieldInput.endDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("startDate")}
            onblurFn={() => textInputOnBlur("startDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.startDate,
              datepickerError:
                error.startDate || error.startDateLaterThanEndDate,
            }}
          />
          <Datepicker
            label={t("page.finance.fxRate.exchange.end.date.label")}
            errorMessage={
              error.startDateLaterThanEndDate
                ? t("page.finance.fxRate.exchange.end.date.error.message.2")
                : t("page.finance.fxRate.exchange.end.date.error.message")
            }
            type="end"
            value={searchFieldInput.endDate}
            limitValue={searchFieldInput.startDate}
            onchangeFn={transactionDateOnChange}
            onfocusFn={() => textInputOnFocus("endDate")}
            onblurFn={() => textInputOnBlur("endDate", dateFormatValidator)}
            sxParams={{
              datepickerFocus: focus.endDate,
              datepickerError: error.endDate || error.startDateLaterThanEndDate,
            }}
          />
          <Selection
            label={t("page.finance.fxRate.transaction.currency.label")}
            selectionValue={searchFieldInput.currencyCode}
            onchangeFn={(e) => textInputOnchange(e, "currencyCode")}
            onfocusFn={() => textInputOnFocus("currencyCode")}
            onblurFn={() => textInputOnBlur("currencyCode")}
            options={transactionCurrencyOptions((e) => e.key !== "hkd")}
            sxParams={{ selectionFocus: focus.currencyCode }}
          />
        </Grid>
        <SearchFieldButtonBar
          loading={loading || searching}
          collapse={collapse}
          handleCollapse={handleSearchFieldCollapse}
          resetBtn
          handleReset={handleReset}
          searchBtn
          handleSearch={handleSearch}
        />
        <Divider margin={32} />
      </div>
    );
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {searchField()}
        <FunctionalButtonBar
          loading={loading || searching}
          createBtn
          handleCreate={openCreateDialog}
          customBtn
          customBtnName={t("page.finance.fxRate.fx.rate.config.button.name")}
          customBtnIcon={fxRateConfigIcon()}
          handleCustomBtn={openConfigDialog}
          refreshBtn
          handleRefresh={handleSearch}
          columnDisplayBtn
          columnDisplayProps={{
            columns,
            columnsArray,
            setColumnsArray,
            rememberPreference: "finance-fx-rate",
          }}
        />
        <div
          className={[s.datatableContainer, loading ? s.loadingNow : ""].join(
            " ",
          )}
        >
          {loading ? (
            <Spin />
          ) : (
            <Datatable
              columns={columns}
              columnsPreference={columnsArray}
              datasource={datasource.data}
              total={datasource.total}
              pagination={pagination}
              setCurrentPage={setCurrentPage}
              setPageSize={setPageSize}
              pageSizeInfoRef={pageSizeInfoRef}
              handleSearch={handleSearch}
              search={search}
            />
          )}

          {searching && <Spin type={"search"} />}
        </div>
        <SimpleDialog
          open={isChangeDialogOpen}
          dialogOncloseFn={closeChangeDialog}
          title={t("page.finance.fxRate.changeFunction.dialogTitle")}
          content={changeContent()}
          confirmFn={confirmChange}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
        <SimpleDialog
          open={isCreateDialogOpen}
          dialogOncloseFn={closeCreateDialog}
          title={t("page.finance.fxRate.create.function.dialog.title")}
          content={createContent()}
          confirmFn={confirmCreate}
          positiveAction={t("component.simple.dialog.action.3")}
          negativeAction={t("component.simple.dialog.action.4")}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
        <EditDialog
          open={isConfigDialogOpen}
          dialogOncloseFn={closeConfigDialog}
          title={t("page.finance.fxRate.fx.rate.config.dialog.title")}
          content={configContent()}
          confirmFn={handleConfigDialogSaveBtn}
          isDialogEditing={isConfigEditing}
          editFn={handleConfigDialogEditBtn}
          negativeActionDisabled={dialogLoading}
          positiveActionLoading={dialogLoading}
        />
      </div>
    </div>
  );
};

export default React.memo(FxRate);
